import * as accessor from "../nodeAccessor";
import * as factory from "../nodeFactory";
import * as pathUtils from "./pathUtils";
import * as systemUtils from "./systemUtils";
import * as typeUtils from "./typeUtils";

//------------------------------------------------------------------------

export const getFirstFunctionIndex = (root, index) => {
  const _functions = getFunctions(root);

  if (_functions.length === 0) {
    return "";
  }

  return getID(_functions[index]);
};

export const getLastFunction = (root) => {
  const _functions = accessor.getFunctions(root);

  if (_functions.length === 0) {
    return null;
  }

  return _functions.at(-1);
};

export const getLastParameter = (container) => {
  const _parameters = accessor.getParameters(container);

  if (_parameters.length === 0) {
    return null;
  }

  return _parameters.at(-1);
};

export const getSiblingNames = (parent, type) => {
  const results = [];

  if (type === typeUtils.getTypeFunction()) {
    results.push(...getFunctions(parent).map(e => getName(e)));
  } else if (type === typeUtils.getTypeParameter()) {
    results.push(...getParameters(parent).map(e => getName(e)));
  } else if (type === typeUtils.getTypeChoice()) {
    results.push(...getChoices(parent).map(e => getName(e)));
  } else if (type === typeUtils.getTypeCondition()) {
    results.push(...getConditions(parent).map(e => getName(e)));
  } else if (type === typeUtils.getTypeConditionConstraint()) {
    results.push(...getConditions(parent).filter(e => getType(e) === typeUtils.getTypeConditionConstraint()).map(e => getName(e)));
  } else if (type === typeUtils.getTypeConditionAssignment()) {
    results.push(...getConditions(parent).filter(e => getType(e) === typeUtils.getTypeConditionAssignment()).map(e => getName(e)));
  } else if (type === typeUtils.getTypeConditionAlias()) {
    results.push(...getConditions(parent).filter(e => getType(e) === typeUtils.getTypeConditionAlias()).map(e => getName(e)));
  }

  return results.filter(e => e !== "");
}

export const getPreviousSiblingFunction = (container, element) => {
  return getPreviousSibling(element, getFunctions(container));
};

export const getPreviousSiblingParameter = (container, element) => {
  return getPreviousSibling(element, getParameters(container));
};

export const getPreviousSiblingChoice = (container, element) => {
  return getPreviousSibling(element, getChoices(container));
};

const getPreviousSibling = (container, siblings) => {
  let i = 0;
  for (; i < siblings.length; i++) {
    if (getID(siblings[i]) === getID(container)) {
      if (i === 0) {
        return null;
      } else {
        return siblings[i - 1];
      }
    }
  }

  throw Error(`The element '${getID(container)}' is not a child of the parent!`);
};

export const hasNestedOutputParameters = (container) => {
  if (!hasParameters(container)) {
    return false;
  }

  let _results = false;

  visitAllParameters(container, (e) => {
    if (systemUtils.isNonNullish(getValueDefault(e))) {
      _results = true;
    }
  });

  return _results;
};

export const getIdChildWithName = (container, name) => {
  if (hasParameters(container)) {
    return getID(getParameters(container).find((e) => getName(e) === name));
  }

  if (hasChoices(container)) {
    return getID(getChoices(container).find((e) => getName(e) === name));
  }

  return null;
};

export const compareLevels = (node1, node2, relVertical) => {
  const comparison = pathUtils.compareLevels(getID(node1), getID(node2));

  if (relVertical) {
    return comparison - 1;
  }

  return comparison;
};

//------------------------------------------------------------------------

export const copy = (ref, config = {}) => {
  const { name, idParent } = config;
  const _idTrack = new Map();

  const _node = factory.copy(ref);

  if (systemUtils.isNonNullish(name)) {
    accessor.setName(_node, name);
  }

  if (systemUtils.isNonNullish(name) || idParent) {
    updateNode(_node, idParent, _idTrack);
  }

  return { data: _node, idTrack: _idTrack };
};

export const updateNode = (node, idParent, idTrack) => {
  const _idTrack = idTrack ?? new Map();

  const _idParent = idParent ?? pathUtils.getIdParent(accessor.getID(node));

  updateIdNode(node, _idParent, _idTrack);

  updateChildrenParameters(node, _idTrack);
  updateChildrenChoices(node, _idTrack);
  updateChildrenConditions(node, _idTrack);
};

const updateChildrenParameters = (node, idTrack) => {
  const parameters = accessor.getParameters(node);

  for (let i = 0; i < parameters.length; i++) {
    updateNode(parameters[i], accessor.getID(node), idTrack);
  }
};

const updateChildrenChoices = (node, idTrack) => {
  const choices = accessor.getChoices(node);

  for (let i = 0; i < choices.length; i++) {
    updateNode(choices[i], accessor.getID(node), idTrack);
  }
};

const updateChildrenConditions = (node, idTrack) => {
  const conditions = accessor.getConditions(node);

  for (let i = 0; i < conditions.length; i++) {
    updateNode(conditions[i], accessor.getID(node), idTrack);
  }
};

export const updateIdNode = (node, idParent, idTrack) => {
  const idOrigin = getID(node);

  const name = getName(node);
  const type = getType(node);

  const typeSimplified = typeUtils.getTypeSimplified(type);

  const id = pathUtils.getIdElement(name, typeSimplified, idParent);

  setID(node, id);

  if (idTrack) {
    idTrack.set(idOrigin, id);
    idTrack.set(id, idOrigin);
  }
};

//------------------------------------------------------------------------

export const visitElements = (node, action) => {
  if (node && systemUtils.isObject(node)) {
    if (!systemUtils.isArray(node)) {
      if (getID(node)) {
        action(node);
      }
    }
  } else {
    return;
  }

  for (const [, value] of Object.entries(node)) {
    if (systemUtils.isObject(value)) {
      visitElements(value, action);
    }
  }
};

export const visitParameters = (node, action) => {
  getParameters(node).forEach((e) => {
    action(e);
    visitParameters(e, action);
  });
};

export const visitAllParameters = (node, action) => {
  getParameters(node).forEach((e) => {
    action(e);
    visitAllParameters(e, action);
  });

  getFunctions(node).forEach((e) => visitAllParameters(e, action));
};

//------------------------------------------------------------------------

export const addIdReference = (parameter, ref) => {
  const _reference = accessor.getIdReferences(parameter);

  if (!_reference.find((e) => e === ref)) {
    _reference.push(ref);
  }

  accessor.setIdReferences(parameter, _reference);
};

export const updateIdReference = (parameter, refOld, refNew) => {
  removeIdReference(parameter, refOld);
  addIdReference(parameter, refNew);
};

export const removeIdReference = (parameter, ref) => {
  const _reference = accessor.getIdReferences(parameter).filter((e) => e !== ref);

  accessor.setIdReferences(parameter, _reference.length === 0 ? null : _reference);
};

//------------------------------------------------------------------------

export const getConditionsAliases = (element) => {
  return getConditionType(element, typeUtils.getTypeConditionAlias());
};

export const getConditionsConstraints = (element) => {
  return getConditionType(element, typeUtils.getTypeConditionConstraint());
};

export const getConditionsAssignments = (element) => {
  return getConditionType(element, typeUtils.getTypeConditionAssignment());
};

const getConditionType = (element, type) => {
  const logic = accessor.getConditions(element);

  return logic.filter((e) => accessor.getType(e) === type);
};

//------------------------------------------------------------------------

export const isAbstract = (choice) => {
  return hasChoices(choice);
};

export const hasChildren = (container) => {
  return hasChoices(container) || hasParameters(container) || hasFunctions(container);
};

export const hasFunctions = (root) => {
  return accessor.getFunctions(root).length > 0;
};

export const hasParameters = (container) => {
  return accessor.getParameters(container).length > 0;
};

export const hasChoices = (container) => {
  return accessor.getChoices(container).length > 0;
};

export const isParameterDefault = (node) => {
  return !systemUtils.isNullish(getValueDefault(node));
};

export const isParameterLinked = (node) => {
  return !systemUtils.isNullish(getIdTarget(node));
};

export const isParameterReference = (node) => {
  return getIdReferences(node).length !== 0;
};

//------------------------------------------------------------------------

export const modifyFunctions = (root, fun, replace = true) => {
  const functions = getFunctions(root);

  const results = fun(functions);

  setFunctions(root, replace ? results : functions);

  return root;
};

export const modifyParameters = (container, fun, replace = true) => {
  const parameters = getParameters(container);

  const results = fun(parameters);

  setParameters(container, replace ? results : parameters);

  return container;
};

export const modifyChoices = (container, fun, replace = true) => {
  const choices = getChoices(container);

  const results = fun(choices);

  setChoices(container, replace ? results : choices);

  return container;
};

export const modifyConditions = (container, fun, replace = true) => {
  const conditions = getConditions(container);

  const results = fun(conditions);

  setConditions(container, replace ? results : conditions);

  return container;
};

//------------------------------------------------------------------------
// PASSTHROUGH - ACCESSOR
//------------------------------------------------------------------------

export const setID = (node, id) => {
  accessor.setID(node, id);
};

export const setUUID = (node, uuid) => {
  accessor.setUUID(node, uuid);
};

export const setName = (node, name, update = false) => {
  accessor.setName(node, name);

  const _idTrack = new Map();

  if (update) {
    updateNode(node, getIdParent(node), _idTrack);
  }

  return _idTrack;
};

export const setType = (node, type) => {
  accessor.setType(node, type);
};

export const setLabel = (node, label) => {
  accessor.setLabel(node, label);
};

export const setComment = (node, comment) => {
  accessor.setComment(node, comment);
};

export const setTimeCreate = (node, time) => {
  accessor.setTimeCreate(node, time);
};

export const setTimeUpdate = (node, time) => {
  accessor.setTimeUpdate(node, time);
};

export const setFunctions = (root, functions) => {
  accessor.setFunctions(root, functions);
};

export const setIdTarget = (parameter, idTarget) => {
  accessor.setIdTarget(parameter, idTarget);
};

export const setIdReferences = (parameter, reference) => {
  accessor.setIdReferences(parameter, reference);
};

export const setValueDefault = (parameter, valueDefault) => {
  if (getType(parameter) !== typeUtils.getTypeParameterOutput()) {
    throw new Error("Cannot change the output value - Incompatible parameter types!");
  }

  accessor.setValueDefault(parameter, valueDefault);
};

export const setValue = (choice, value) => {
  accessor.setValue(choice, value);
};

export const setParameters = (container, parameters) => {
  if (parameters === null) {
    accessor.setParameters(container, null);
    return;
  }

  if (!systemUtils.isArray(parameters)) {
    throw new Error("The value must be an array - 'setParameters()'.");
  }

  accessor.setParameters(container, parameters.length === 0 ? null : parameters);
};

export const setChoices = (container, choices) => {
  if (choices === null) {
    accessor.setChoices(container, null);
    return;
  }

  if (!systemUtils.isArray(choices)) {
    throw new Error("The value must be an array - 'setChoices()'.");
  }

  accessor.setChoices(container, choices.length === 0 ? null : choices);
};

export const setConditions = (container, conditions) => {
  if (conditions === null) {
    accessor.setConditions(container, null);
    return;
  }

  if (!systemUtils.isArray(conditions)) {
    throw new Error("The value must be an array - 'setConditions()'.");
  }

  accessor.setConditions(container, conditions.length === 0 ? null : conditions);
};

export const setExpression = (condition, expression) => {
  accessor.setExpression(condition, expression);
};

//------------------------------------------------------------------------

export const getID = (node) => {
  return accessor.getID(node);
};

export const getUUID = (node) => {
  return accessor.getUUID(node);
};

export const getName = (node) => {
  return accessor.getName(node);
};

export const getType = (node) => {
  return accessor.getType(node);
};

export const getLabel = (node) => {
  return accessor.getLabel(node);
};

export const getComment = (node) => {
  return accessor.getComment(node);
};

export const getTimeCreate = (node) => {
  return accessor.getTimeCreate(node);
};

export const getTimeUpdate = (node) => {
  return accessor.getTimeUpdate(node);
};

export const getFunctions = (root) => {
  return accessor.getFunctions(root);
};

export const getIdTarget = (parameter) => {
  return accessor.getIdTarget(parameter);
};

export const getIdReferences = (parameter) => {
  return accessor.getIdReferences(parameter);
};

export const getValueDefault = (parameter) => {
  return accessor.getValueDefault(parameter);
};

export const getValue = (choice) => {
  return accessor.getValue(choice);
};

export const getParameters = (container) => {
  return accessor.getParameters(container);
};

export const getChoices = (container) => {
  return accessor.getChoices(container);
};

export const getConditions = (container) => {
  return accessor.getConditions(container);
};

export const getExpression = (container) => {
  return accessor.getExpression(container);
};

//------------------------------------------------------------------------
// PASSTHROUGH - PATH
//------------------------------------------------------------------------

export const isRoot = (node) => {
  return pathUtils.isRoot(accessor.getID(node));
};

export const isFunction = (node) => {
  return pathUtils.isFunction(accessor.getID(node));
};

export const isParameter = (node) => {
  return pathUtils.isParameter(accessor.getID(node));
};

export const isChoice = (node) => {
  return pathUtils.isChoice(accessor.getID(node));
};

export const isLogic = (node) => {
  return pathUtils.isCondition(accessor.getID(node));
};

export const isParameterTop = (node) => {
  return pathUtils.isParameterTop(accessor.getID(node));
};

export const isParameterTopGlobal = (node) => {
  return pathUtils.isParameterTopGlobal(accessor.getID(node));
};

export const isParameterTopLocal = (node) => {
  return pathUtils.isParameterTopLocal(accessor.getID(node));
};

export const isParameterGlobal = (node) => {
  return pathUtils.isParameterGlobal(accessor.getID(node));
};

export const isParameterNestedGlobal = (node) => {
  return pathUtils.isParameterNestedGlobal(accessor.getID(node));
};

export const isParameterLocal = (node) => {
  return pathUtils.isParameterLocal(accessor.getID(node));
};

export const isParameterNestedLocal = (node) => {
  return pathUtils.isParameterNestedGlobal(accessor.getID(node));
};

export const getIdParent = (node) => {
  return pathUtils.getIdParent(accessor.getID(node));
};

export const getNodeByPath = (root, path) => {
  let currentNode = root;
  const segments = path.split(pathUtils.getIdSeparator());

  for (const segment of segments) {
    if (!currentNode) return null;
    currentNode = currentNode[segment];
  }

  return currentNode;
};

/**
 * Gets the parent node of a given node in the tree structure.
 * @param {Object} root - Root node to start the search from.
 * @param {Object} node - The node whose parent we want to find.
 * @returns {Object|null} - The parent node or null if not found.
 */
export const getParentNode = (root, node) => {
  const _nodeId = getID(node);
  const _parentId = pathUtils.getIdParent(_nodeId);

  if (pathUtils.isRoot(_parentId)) {
    return null;
  }

  let _parentNode = null;
  visitElements(root, (_candidate) => {
    if (getID(_candidate) === _parentId) {
      _parentNode = _candidate;
    }
  });

  if (!_parentNode) {
    console.error(
      `Critical error: Parent node with ID '${_parentId}' not found for node '${_nodeId}'.`
    );
  }

  return _parentNode;
};

/**
 * Gets all ancestor nodes of a node up to the root
 * @param {Object} root - Root node to start search from
 * @param {Object|string} node - The node or node ID whose ancestors we want to find
 * @returns {Array<Object>} - Array of ancestor nodes (immediate parent first, root last)
 */
export const getAncestorNodes = (root, node) => {
  const _ancestors = [];
  let _nodeCurrent = node;

  let _parent = getParentNode(root, _nodeCurrent);

  while (_parent) {
    _ancestors.push(_parent);

    if (isRoot(_parent)) {
      break;
    }

    _parent = getParentNode(root, _parent);
  }

  return _ancestors;
};
